import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, checkIsAdmin } from '../lib/firebase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Lock } from 'lucide-react';
import Logo from '../components/Logo';

type LoginForm = {
  email: string;
  password: string;
};

const ADMIN_EMAIL = 'hola@iznoval.com';
const ADMIN_PASSWORD = '123456';

export default function Login() {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm<LoginForm>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Pre-fill admin credentials in development
    if (process.env.NODE_ENV === 'development') {
      setValue('email', ADMIN_EMAIL);
      setValue('password', ADMIN_PASSWORD);
    }

    const checkAuthStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        const isAdmin = await checkIsAdmin(user.uid);
        if (isAdmin) {
          navigate('/admin');
        }
      }
    };

    checkAuthStatus();
  }, [navigate, setValue]);

  const handleAdminCheck = async (uid: string) => {
    const isAdmin = await checkIsAdmin(uid);
    if (!isAdmin) {
      await auth.signOut();
      throw new Error('No tienes permisos de administrador');
    }
    return true;
  };

  const onSubmit = async (data: LoginForm) => {
    setIsSubmitting(true);
    try {
      // Verificar si las credenciales coinciden con las del administrador
      if (data.email !== ADMIN_EMAIL) {
        throw new Error('Email no autorizado');
      }

      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      await handleAdminCheck(userCredential.user.uid);
      toast.success('¡Bienvenido!');
      navigate('/admin');
    } catch (error: any) {
      console.error('Error de login:', error);
      let errorMessage = 'Error al iniciar sesión';
      
      if (error.message === 'Email no autorizado') {
        errorMessage = 'Email no autorizado';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Email o contraseña incorrectos';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Usuario deshabilitado';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Usuario no encontrado';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Contraseña incorrecta';
      } else if (error.message === 'No tienes permisos de administrador') {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      
      // Verificar si el email es el autorizado
      if (result.user.email !== ADMIN_EMAIL) {
        await auth.signOut();
        throw new Error('Email no autorizado');
      }

      await handleAdminCheck(result.user.uid);
      toast.success('¡Bienvenido!');
      navigate('/admin');
    } catch (error: any) {
      console.error('Error con Google login:', error);
      toast.error(error.message === 'Email no autorizado' ? 
        'Este email no está autorizado' : 
        'Error al iniciar sesión con Google'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="bg-primary py-4 px-6 flex items-center">
        <Logo />
        <span className="text-white ml-4">Panel de Administración</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-primary">Panel de Administración</h1>
            <p className="text-gray-600">Accede para gestionar el concurso</p>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
            className="w-full bg-white border-2 border-gray-200 hover:bg-gray-50 text-gray-700 py-3 rounded-lg transition-colors mb-6 flex items-center justify-center space-x-2"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>{isSubmitting ? 'Conectando...' : 'Continuar con Google'}</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">O continúa con email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                {...register('email', { 
                  required: 'El email es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Email inválido'
                  }
                })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="admin@arbolnavidad.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña
              </label>
              <input
                type="password"
                {...register('password', { 
                  required: 'La contraseña es requerida'
                })}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="••••••••"
                disabled={isSubmitting}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Accediendo...' : 'Acceder'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}